import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Video, 
  Camera, 
  Clock, 
  Heart,
  Play,
  ArrowRight,
  Sparkles,
  Users,
  Star
} from 'lucide-react';

const LiveStreamHeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 text-blue-200 animate-bounce">
        <Video className="h-8 w-8" />
      </div>
      <div className="absolute top-32 right-16 text-purple-200 animate-pulse">
        <Camera className="h-6 w-6" />
      </div>
      <div className="absolute bottom-20 left-20 text-blue-300 animate-bounce delay-1000">
        <Users className="h-10 w-10" />
      </div>
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center max-w-5xl mx-auto">
          {/* HiveShare Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/c79faca6-99d9-45c3-826d-610a40f2e500/public"
              alt="HiveShare Live Logo"
              className="h-28 w-auto mx-auto mb-6"
            />
          </motion.div>

          {/* Brand Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <Badge variant="secondary" className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 border-blue-200 px-6 py-3 text-lg font-medium">
              <Sparkles className="h-5 w-5 mr-2" />
              HiveShare Live by Hivetivity
            </Badge>
          </motion.div>

          {/* Announcement Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 text-sm font-semibold animate-pulse">
              🚀 NEW: Revolutionary Live Streaming for Activity Centers
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-6xl lg:text-8xl font-extrabold text-gray-900 mb-6 leading-tight"
          >
            Turn Every Event Into a{' '}
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-pulse">
              Revenue Stream
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-3xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed font-medium"
          >
            Stream dance recitals, gymnastics meets, and music performances live to families worldwide. 
            <span className="font-bold text-gray-800"> $25-75 per event. 400-1500% profit margins.</span>
          </motion.p>

          {/* Explosive Value Props */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-blue-100">
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-3 w-fit mx-auto mb-4">
                <Camera className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Professional Quality</h3>
              <p className="text-gray-600">HD streaming with automatic recordings</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-purple-100">
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 w-fit mx-auto mb-4">
                <Heart className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Family Connection</h3>
              <p className="text-gray-600">Grandparents never miss a moment</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-blue-100">
              <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg p-3 w-fit mx-auto mb-4">
                <Star className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Instant Revenue</h3>
              <p className="text-gray-600">Turn events into profit centers</p>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-center justify-center gap-8 mb-12 text-sm text-gray-500 flex-wrap"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-green-500" />
              <span className="font-medium">Setup in 10 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Professional results guaranteed</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-500" />
              <span className="font-medium">Family-friendly pricing</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white px-12 py-6 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 animate-pulse"
            >
              Start Live Streaming Now
              <ArrowRight className="h-6 w-6 ml-3" />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-3 border-blue-300 text-blue-700 hover:bg-blue-50 px-12 py-6 text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Play className="h-6 w-6 mr-3" />
              Watch Live Demo
            </Button>
          </motion.div>

          {/* Revenue Highlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl p-6 max-w-2xl mx-auto shadow-2xl"
          >
            <p className="text-lg font-bold mb-2">💰 Revenue Example:</p>
            <p className="text-2xl font-extrabold">
              2-hour recital × 50 families = $1,500 revenue
            </p>
            <p className="text-sm opacity-90 mt-2">
              (Cost: $10 | Profit: $1,490 | Margin: 99.3%)
            </p>
          </motion.div>

          {/* Trust Statement */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-8 text-lg text-gray-500 font-medium"
          >
            ✨ No contracts • 🎥 Money-back guarantee • 🚀 Launch this week
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default LiveStreamHeroSection;